import { Autocomplete, Box, styled, Typography } from '@mui/material'
import React, { forwardRef, memo, useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { Virtuoso } from 'react-virtuoso'
import CustomTextField from '../textfield'
import RequiredLabelComponent from '../required-label-component'

// styled component
const ErrorText = styled(Typography)(() => ({
  fontSize: '0.8125rem',
  marginBottom: '0.25rem',
  lineHeight: '1.154',
  color: 'var(--error-main)',
  marginTop: '4px'
}))

const VirtualisedList = memo(
  forwardRef((props, ref) => {
    const { children, ...rest } = props

    const items = useMemo(() => {
      return children ? React.Children.toArray(children) : []
    }, [children])

    return (
      <Box ref={ref}>
        <Box {...rest}>
          <Virtuoso
            style={{ height: 300 }}
            totalCount={items?.length || 0}
            itemContent={(index) => {
              return React.cloneElement(items[index], {
                dataindex: index
              })
            }}
          />
        </Box>
      </Box>
    )
  })
)

const CustomAutocomplete = memo(
  ({
    needVirtualization,
    options,
    renderInput,
    error,
    placeholder = '',
    inputLabelAdornment = null,
    ...rest
  }) => {
    return (
      <Autocomplete
        options={options || []}
        renderInput={(params) =>
          renderInput ? (
            renderInput(params)
          ) : (
            <CustomTextField
              error={error}
              placeholder={placeholder}
              {...params}
              {...(inputLabelAdornment
                ? {
                    InputProps: {
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          {inputLabelAdornment}
                          {params.InputProps?.startAdornment}
                        </>
                      )
                    }
                  }
                : {})}
            />
          )
        }
        {...(needVirtualization ? { ListboxComponent: VirtualisedList } : {})}
        {...rest}
      />
    )
  }
)

export const INPUTLABEL_SX = {
  minWidth: '170px',
  '& .MuiInputBase-root': {
    height: '31px',
    border: '1px solid var(--grey-400)',
    flexWrap: 'nowrap'
  },
  '& .MuiInputBase-root .MuiAutocomplete-input': {
    fontSize: '13px',
    lineHeight: 1.2,
    minWidth: '20px'
  },
  '& .MuiInputBase-root .MuiAutocomplete-input::placeholder': {
    color: 'var(--text-secondary)',
    opacity: 1
  }
}

const VirtualizedAutocomplete = ({
  name,
  control,
  label,
  renderInput,
  options = [],
  error = false,
  helperText = '',
  required = false,
  tooltipText = '',
  needVirtualization = true,
  fieldClassName = '',
  inputLabel = '',
  inputLabelStyle = {},
  autoCompleteProps = {}
}) => {
  const inputLabelAdornment = useMemo(() => {
    if (!inputLabel) return null
    return (
      <Typography
        component="span"
        sx={{
          fontSize: '0.8125rem',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          mr: 1,
          ml: 2,
          lineHeight: 1,
          ...inputLabelStyle
        }}
      >
        {inputLabel}:
      </Typography>
    )
  }, [inputLabel, inputLabelStyle])

  const mergedAutoCompleteProps = useMemo(() => {
    if (!inputLabel) return autoCompleteProps
    return {
      ...autoCompleteProps,
      sx: { ...INPUTLABEL_SX, ...autoCompleteProps?.sx }
    }
  }, [inputLabel, autoCompleteProps])

  return control ? (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange, onBlur } }) => (
        <Box className={fieldClassName}>
          {label && (
            <RequiredLabelComponent
              label={label}
              error={error}
              required={required}
              tooltipText={tooltipText}
            />
          )}
          <CustomAutocomplete
            value={value}
            onBlur={onBlur}
            options={options}
            onChange={(_, values) => {
              onChange(values)
            }}
            renderInput={renderInput}
            needVirtualization={needVirtualization}
            error={error}
            inputLabelAdornment={inputLabelAdornment}
            {...mergedAutoCompleteProps}
          />
          {error && helperText && <ErrorText>{helperText}</ErrorText>}
        </Box>
      )}
    />
  ) : (
    <Box>
      {label && (
        <RequiredLabelComponent
          label={label}
          error={error}
          required={required}
          tooltipText={tooltipText}
        />
      )}
      <CustomAutocomplete
        options={options}
        renderInput={renderInput}
        needVirtualization={needVirtualization}
        inputLabelAdornment={inputLabelAdornment}
        {...mergedAutoCompleteProps}
      />
      {error && helperText && <ErrorText>{helperText}</ErrorText>}
    </Box>
  )
}

export default memo(VirtualizedAutocomplete)
